<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

/**
 * Modal mínimo e acessível: `role="dialog"`, `aria-modal`, nome pelo título,
 * foco inicial dentro do diálogo, `Escape` e clique no fundo fecham, `Tab`
 * fica contido e o foco volta a quem abriu. Montar com `v-if`: abrir é montar,
 * fechar é desmontar.
 */
defineProps<{ title: string; wide?: boolean }>();
const emit = defineEmits<{ close: [] }>();

const dialog = ref<HTMLElement | null>(null);
const backdrop = ref<HTMLElement | null>(null);
const titleId = `modal-title-${Math.random().toString(36).slice(2, 10)}`;
let opener: HTMLElement | null = null;
let previousBodyOverflow = '';
let background: { element: HTMLElement; inert: boolean; ariaHidden: string | null }[] = [];

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(): HTMLElement[] {
  return dialog.value === null ? [] : [...dialog.value.querySelectorAll<HTMLElement>(FOCUSABLE)];
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.preventDefault();
    emit('close');
    return;
  }
  if (event.key !== 'Tab') return;
  const items = focusables();
  if (items.length === 0) {
    event.preventDefault();
    dialog.value?.focus();
    return;
  }
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const active = document.activeElement;
  if (event.shiftKey && (active === first || active === dialog.value)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

onMounted(async () => {
  opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  background = [...(backdrop.value?.parentElement?.children ?? [])]
    .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== backdrop.value)
    .map((element) => ({ element, inert: element.inert, ariaHidden: element.getAttribute('aria-hidden') }));
  for (const item of background) {
    item.element.inert = true;
    item.element.setAttribute('aria-hidden', 'true');
  }
  await nextTick();
  const preferred = dialog.value?.querySelector<HTMLElement>('[data-autofocus]');
  (preferred ?? focusables()[0] ?? dialog.value)?.focus();
});

onBeforeUnmount(() => {
  document.body.style.overflow = previousBodyOverflow;
  for (const item of background) {
    item.element.inert = item.inert;
    if (item.ariaHidden === null) item.element.removeAttribute('aria-hidden');
    else item.element.setAttribute('aria-hidden', item.ariaHidden);
  }
  if (opener !== null && opener.isConnected) opener.focus();
});

</script>

<template>
  <div
    ref="backdrop"
    class="modal-backdrop"
    @click.self="emit('close')"
  >
    <div
      ref="dialog"
      class="modal"
      :class="{ 'modal--wide': wide }"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
      @keydown="onKeydown"
    >
      <header class="modal__header">
        <h2 :id="titleId">
          {{ title }}
        </h2>
        <button
          type="button"
          class="modal__close"
          aria-label="Fechar"
          @click="emit('close')"
        >
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            width="18"
            height="18"
          ><path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          /></svg>
        </button>
      </header>
      <div class="modal__body">
        <slot />
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: hsl(222 47% 11% / 0.45);
}
.modal {
  display: flex;
  flex-direction: column;
  width: min(32rem, 100%);
  max-height: calc(100dvh - 2rem);
  background: #fff;
  color: var(--foreground, hsl(222 47% 11%));
  border: 1px solid var(--border, hsl(214 20% 90%));
  border-radius: var(--radius, 14px);
  box-shadow: 0 20px 45px hsl(222 47% 11% / 0.18);
}
.modal--wide { width: min(60rem, 100%); }
.modal:focus { outline: none; }
.modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border, hsl(214 20% 90%));
}
.modal__header h2 { margin: 0; font-size: 1.05rem; font-weight: 600; }
.modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  color: inherit;
  cursor: pointer;
}
.modal__close:hover { background: var(--secondary, hsl(210 20% 94%)); }
.modal__body { padding: 1rem 1.25rem 1.25rem; overflow: auto; }
@media (max-width: 640px) {
  .modal-backdrop { padding: 0.5rem; }
  .modal, .modal--wide { width: 100%; max-height: calc(100dvh - 1rem); }
}
</style>
